import {useCallback, useEffect, useMemo, useState} from "react";
import {useIntl} from "react-intl";

import {INavigationLink} from "@housebets/housebets-x-package/lib/components/feature/lists/NavigationBar/types";
import {Baccarat, BlackJack, GameShows, Hot, LiveIcons2, Original, Roulette, SlotsIcon2} from "@housebets/housebets-x-package/lib/components/icons";

import {GameInfo} from "app/types/game";
import apiCasino from "app/api/casino";

import {useSearchParams} from "react-router-dom";
import {useDebounce} from "app/hooks/useDebounce";

export const useSearchGameModalData = () => {
    const {$t} = useIntl();
    const [activeTagId, setActiveTagId] = useState("0");
    const [isLoading, setIsLoading] = useState(false);
    const [gameList, setGameList] = useState<GameInfo[]>([]);

    const [keyWord, setKeyWord] = useState<string>("");
    const [current_page, setCurrent_page] = useState(-1);
    const [last_page, setLast_page] = useState(-1);
    const [total, setTotal] = useState(0);

    const listMeta = useMemo(() => {
        return {
            current_page: gameList.length,
            last_page: total,
            per_page: 10,
            prev_page: 0,
            total: total,
        };
    }, [gameList, total]);

    const [searchParams] = useSearchParams();
    const tabParams = searchParams.get("tab");

    useEffect(() => {
        if (tabParams) {
            const currentSelected = navigationModalList.find((item) => item.label.toLowerCase() === decodeURIComponent(tabParams).toLowerCase());
            if (currentSelected) {
                setActiveTagId(currentSelected?.id);
            }
        }
    }, [tabParams]);

    const navigationModalList = useMemo<INavigationLink[]>(() => {
        return [
            {
                icon: null,
                id: "0",
                label: $t({id: "All"}),
            },
            {
                icon: <Original />,
                id: "1",
                label: $t({id: "MiniGames"}),
                params: "1013",
            },
            {
                icon: <SlotsIcon2 />,
                id: "2",
                label: $t({id: "Slots"}),
                params: "2",
            },
            {
                icon: <LiveIcons2 />,
                id: "3",
                label: $t({id: "LiveCasino"}),
                params: "3",
            },
            {
                icon: <BlackJack />,
                id: "4",
                label: $t({id: "BlackJack"}),
                params: "",
            },
            {
                icon: <Roulette />,
                id: "5",
                label: $t({id: "Roulette"}),
                params: "",
            },
            {
                icon: <Baccarat />,
                id: "6",
                label: $t({id: "Baccarat"}),
                params: "",
            },
            {
                icon: <GameShows />,
                id: "7",
                label: $t({id: "GameShows"}),
                params: "",
            },
            {
                icon: <Hot />,
                id: "hot",
                label: $t({id: "Hot"}),
            },
        ];
    }, []);

    const initData = useCallback(() => {
        setKeyWord("");
        setGameList([]);
        setCurrent_page(1);
        setLast_page(1);
    }, []);

    const onLoadMore = useCallback(() => {
        if (isLoading || current_page >= last_page) return;
        setCurrent_page((prev) => prev + 1);
    }, [isLoading, current_page, last_page]);

    const getGameList = async (activeTagId1?: string) => {
        const tagParam = navigationModalList.find((v) => v.id === (activeTagId1 || activeTagId))?.params || "";
        const type = keyWord ? 3 : 4;
        const param = keyWord || tagParam || "3";
        if (!param) return;
        setIsLoading(true);
        try {
            const {status, data} = await apiCasino.getGameList(current_page, 40, type, param);
            if (status === 200 && data.code === 0) {
                const list = data.data.gameList || [];
                list.forEach((v: GameInfo) => {
                    v.additionalInfo = {
                        activeUsers: Math.floor(Math.random() * (1000 - 100 + 1)) + 100,
                    };
                });
                if (current_page === 1) {
                    setGameList(list);
                } else {
                    setGameList([...gameList, ...list]);
                }

                const lastPage = Math.ceil(data.data.totalCount / data.data.pageCount);
                setLast_page(lastPage === 0 ? 1 : lastPage);
                setTotal(data.data.totalCount);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const disposeData = useCallback(() => {
        setGameList([]);
        setCurrent_page(-1);
        setLast_page(-1);
        setIsLoading(false);
        setActiveTagId("0");
        setKeyWord("");
        setTotal(0);
    }, []);

    useEffect(() => {
        getGameList();
    }, [current_page]);

    useDebounce(
        () => {
            if (keyWord.length >= 3) {
                setCurrent_page(1);
                setActiveTagId(navigationModalList[0].id);
                setTimeout(() => {
                    getGameList();
                }, 10);
            }
        },
        500,
        [keyWord],
    );

    return {
        keyWord,
        setKeyWord,
        activeTagId,
        setActiveTagId,
        isLoading,
        setIsLoading,
        navigationModalList,
        gameList,
        setGameList,
        initData,
        getGameList,
        last_page,
        total,
        listMeta,
        onLoadMore,
        disposeData,
    };
};
